/**
 * Feature Track Modal
 * Allows artists to feature their tracks with scheduling and timezone options
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Radio,
  RadioGroup,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Input,
  Select,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  Icon,
  useToast
} from '@chakra-ui/react';
import { FiClock, FiGlobe } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addHours, addDays, format } from 'date-fns';
import { useCreditContext } from '../../context/CreditContext';
import { useFeaturedTracks } from '../../hooks/useFeaturedTracks';

// Common timezones grouped by region
const TIMEZONE_GROUPS = [
  {
    label: 'Americas',
    zones: [
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'America/Anchorage', label: 'Alaska (AKT)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
      { value: 'America/Toronto', label: 'Toronto (ET)' },
      { value: 'America/Sao_Paulo', label: 'Sao Paulo (BRT)' },
    ]
  },
  {
    label: 'Europe & Africa',
    zones: [
      { value: 'Europe/London', label: 'London (GMT/BST)' },
      { value: 'Europe/Paris', label: 'Paris (CET)' },
      { value: 'Europe/Berlin', label: 'Berlin (CET)' },
      { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
      { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
    ]
  },
  {
    label: 'Asia & Pacific',
    zones: [
      { value: 'Asia/Dubai', label: 'Dubai (GST)' },
      { value: 'Asia/Kolkata', label: 'India (IST)' },
      { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
      { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
      { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
    ]
  }
];

// Get user's local timezone
const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
};

// Convert local date to UTC based on selected timezone
const convertToUTC = (date, timezone) => {
  const dateStr = format(date, 'yyyy-MM-dd HH:mm:ss');
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  const utcDate = new Date(date.getTime() + (date.getTime() - tzDate.getTime()));
  return utcDate;
};

// Format date in a specific timezone for display
const formatInTimezone = (date, timezone) => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const FeatureTrackModal = ({ isOpen, onClose, track, onSuccess }) => {
  const { credits, refetch: refetchCredits } = useCreditContext();
  const { featureTrack, loading } = useFeaturedTracks();
  const toast = useToast();

  // State
  const [startOption, setStartOption] = useState('now');
  const [startDate, setStartDate] = useState(new Date());
  const [duration, setDuration] = useState(24);
  const [timezone, setTimezone] = useState(getUserTimezone());

  // Duration options for slider
  const durationMarks = [1, 2, 3, 6, 12, 24, 48, 72, 168, 720];

  // Get closest duration mark for slider
  const getSliderValue = (hours) => {
    return durationMarks.findIndex(mark => mark === hours) || 5;
  };

  const [sliderValue, setSliderValue] = useState(getSliderValue(24));

  // Update duration when slider changes
  useEffect(() => {
    setDuration(durationMarks[sliderValue]);
  }, [sliderValue]);

  // Computed values
  const startDateTime = useMemo(() => {
    if (startOption === 'now') return new Date();
    return convertToUTC(startDate, timezone);
  }, [startOption, startDate, timezone]);

  const endDateTime = useMemo(() => {
    return addHours(startDateTime, duration);
  }, [startDateTime, duration]);

  const costInCredits = duration;
  const costInDollars = duration * 1.00;
  const availableCredits = credits?.available_credits || 0;
  const hasEnoughCredits = availableCredits >= costInCredits;
  const creditsRemaining = availableCredits - costInCredits;

  // Format duration display
  const formatDuration = (hours) => {
    if (hours < 24) return `${hours} hours`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days} ${days === 1 ? 'day' : 'days'}`;
    return `${days}d ${remainingHours}h`;
  };

  // Get timezone abbreviation for display
  const getTimezoneAbbr = (tz) => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short'
      }).formatToParts(new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value || tz;
    } catch {
      return tz;
    }
  };

  // Handle feature track
  const handleFeature = async () => {
    if (!hasEnoughCredits) {
      toast({
        title: 'Insufficient Credits',
        description: `You need ${costInCredits} credits but only have ${availableCredits}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const startDateISO = startOption === 'scheduled' ? startDateTime.toISOString() : null;
      await featureTrack(track.track_id, costInCredits, startDateISO);
      await refetchCredits();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Feature failed:', error);
    }
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStartOption('now');
      setStartDate(new Date());
      setSliderValue(getSliderValue(24));
      setTimezone(getUserTimezone());
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      {/* Dark theme overrides for react-datepicker */}
      <style>{`
        .react-datepicker {
          background-color: #2f2f2f !important;
          border-color: #3b3b3b !important;
          color: #fff !important;
          font-family: inherit !important;
        }
        .react-datepicker__header {
          background-color: #262626 !important;
          border-bottom-color: #3b3b3b !important;
        }
        .react-datepicker__current-month,
        .react-datepicker__day-name,
        .react-datepicker-time__header {
          color: #cfb182 !important;
        }
        .react-datepicker__day {
          color: #fff !important;
        }
        .react-datepicker__day:hover {
          background-color: #3b3b3b !important;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #cfb182 !important;
          color: #262626 !important;
        }
        .react-datepicker__day--disabled {
          color: #555 !important;
        }
        .react-datepicker__time-container {
          border-left-color: #3b3b3b !important;
        }
        .react-datepicker__time {
          background-color: #2f2f2f !important;
        }
        .react-datepicker__time-list-item {
          color: #fff !important;
        }
        .react-datepicker__time-list-item:hover {
          background-color: #3b3b3b !important;
        }
        .react-datepicker__time-list-item--selected {
          background-color: #cfb182 !important;
          color: #262626 !important;
        }
        .react-datepicker__navigation-icon::before {
          border-color: #cfb182 !important;
        }
        .react-datepicker__triangle {
          display: none !important;
        }
      `}</style>
      <ModalContent bg="secondary.100" color="primary.100">
        <ModalHeader bg="secondary.100" color="dark.900" borderBottom="1px solid" borderColor="secondary.500" fontSize="md">
          Feature: {track?.track_name || 'Unknown'}
        </ModalHeader>
        <ModalCloseButton color="primary.900" />

        <ModalBody bg="secondary.100" py={5}>
          <VStack spacing={5} align="stretch">
            {/* Start Time Options */}
            <FormControl>
              <FormLabel color="primary.100" fontSize="sm" fontWeight="semibold">
                When should this feature start?
              </FormLabel>
              <RadioGroup value={startOption} onChange={setStartOption}>
                <HStack spacing={6}>
                  <Radio value="now" colorScheme="yellow" borderColor="primary.900">
                    <Text color="primary.100" fontSize="sm">Start Immediately</Text>
                  </Radio>
                  <Radio value="scheduled" colorScheme="yellow" borderColor="primary.900">
                    <Text color="primary.100" fontSize="sm">Schedule for Later</Text>
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {/* Date/Time Picker + Timezone (only shown when scheduled) */}
            {startOption === 'scheduled' && (
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel color="primary.100" fontSize="sm" fontWeight="semibold">
                    Start Date & Time
                  </FormLabel>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    customInput={
                      <Input
                        bg="dark.500"
                        color="primary.100"
                        border="1px solid"
                        borderColor="secondary.500"
                        fontSize="sm"
                        _hover={{ borderColor: 'primary.900' }}
                        _focus={{ borderColor: 'dark.900', boxShadow: 'none' }}
                      />
                    }
                  />
                </FormControl>

                {/* Timezone Selector */}
                <FormControl>
                  <FormLabel color="primary.100" fontSize="sm" fontWeight="semibold">
                    <HStack spacing={1}>
                      <Icon as={FiGlobe} boxSize={3.5} />
                      <Text>Timezone</Text>
                    </HStack>
                  </FormLabel>
                  <Select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    bg="dark.500"
                    color="primary.100"
                    border="1px solid"
                    borderColor="secondary.500"
                    fontSize="sm"
                    _hover={{ borderColor: 'primary.900' }}
                    _focus={{ borderColor: 'dark.900', boxShadow: 'none' }}
                  >
                    {TIMEZONE_GROUPS.map((group) => (
                      <optgroup key={group.label} label={group.label} style={{ backgroundColor: '#2f2f2f', color: '#fff' }}>
                        {group.zones.map((tz) => (
                          <option key={tz.value} value={tz.value} style={{ backgroundColor: '#2f2f2f', color: '#fff' }}>
                            {tz.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                  <FormHelperText color="primary.900" fontSize="xs">
                    Schedule up to 30 days in advance
                  </FormHelperText>
                </FormControl>
              </VStack>
            )}

            <Divider borderColor="secondary.500" />

            {/* Duration Slider */}
            <FormControl>
              <FormLabel color="primary.100" fontSize="sm" fontWeight="semibold">
                How long should it be featured?
              </FormLabel>
              <Box px={2}>
                <Slider
                  value={sliderValue}
                  min={0}
                  max={durationMarks.length - 1}
                  step={1}
                  onChange={setSliderValue}
                >
                  <SliderTrack bg="secondary.500" h="6px" borderRadius="full">
                    <SliderFilledTrack bg="dark.900" />
                  </SliderTrack>
                  <SliderThumb boxSize={5} bg="dark.900" borderWidth={2} borderColor="secondary.100" />
                </Slider>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="xs" color="primary.900">1 hour</Text>
                  <HStack spacing={1}>
                    <Icon as={FiClock} color="dark.900" boxSize={3.5} />
                    <Text fontSize="sm" fontWeight="bold" color="dark.900">
                      {formatDuration(duration)}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="primary.900">30 days</Text>
                </HStack>
              </Box>
            </FormControl>

            <Divider borderColor="secondary.500" />

            {/* Cost Summary */}
            <Box bg="dark.500" p={4} borderRadius="md">
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="primary.900">Starts:</Text>
                  <Text fontSize="sm" color="primary.100">
                    {startOption === 'scheduled'
                      ? `${formatInTimezone(startDateTime, timezone)} (${getTimezoneAbbr(timezone)})`
                      : 'Immediately'
                    }
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="primary.900">Ends:</Text>
                  <Text fontSize="sm" color="primary.100">
                    {startOption === 'scheduled'
                      ? `${formatInTimezone(endDateTime, timezone)} (${getTimezoneAbbr(timezone)})`
                      : formatInTimezone(endDateTime, getUserTimezone())
                    }
                  </Text>
                </HStack>
                <Divider borderColor="secondary.500" />
                <HStack justify="space-between">
                  <Text fontSize="sm" color="primary.900">Cost:</Text>
                  <Text fontSize="sm" color="dark.900" fontWeight="bold">
                    {costInCredits} credits (${costInDollars.toFixed(2)})
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="primary.900">Available:</Text>
                  <Text fontSize="sm" color="primary.100">{availableCredits} credits</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="primary.900">Remaining:</Text>
                  <Text fontSize="sm" color={hasEnoughCredits ? 'green.400' : 'red.400'} fontWeight="bold">
                    {hasEnoughCredits ? creditsRemaining : availableCredits} credits
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Insufficient Credits Warning */}
            {!hasEnoughCredits && (
              <Box bg="secondary.500" border="1px solid" borderColor="red.400" borderRadius="md" p={3}>
                <Text fontWeight="bold" fontSize="sm" color="red.400">Insufficient Credits</Text>
                <Text fontSize="xs" color="red.300">
                  You need {costInCredits - availableCredits} more credits. Purchase credits to continue.
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter bg="secondary.100" borderTop="1px solid" borderColor="secondary.500" py={3}>
          <Button variant="ghost" size="sm" mr={3} onClick={onClose} color="primary.900" _hover={{ bg: 'secondary.500' }}>
            Cancel
          </Button>
          <Button
            variant="glassy"
            size="sm"
            onClick={handleFeature}
            isLoading={loading}
            isDisabled={!hasEnoughCredits || loading}
          >
            Feature for {formatDuration(duration)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeatureTrackModal;
